/**
 * MetricsGrid — every counter the summary carries, for people who want the
 * whole board rather than the four headline figures.
 *
 * Same treatment as `QuickMetrics`, one level up in density: a single
 * `GlassCard` holding a divided grid of `KpiTile`s, sized against the WIDGET
 * through the `.widget-metrics-*` container queries so a half-width card gets
 * fewer columns than a full-width one on the same screen.
 *
 * The nine per-metric colour ramps are gone (violet, pink, cyan, slate, sky,
 * teal — six of the nine were not brand tokens at all). `tone` survives on the
 * two counters where colour is information rather than decoration: overdue
 * tasks in `danger` when there are any, completed tasks in `success`.
 */

import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { DashboardOverview } from '../../../store/reducer/DashboardReducer';
import GlassCard from '../../shared/brand/GlassCard';
import { KpiTile, FieldTone } from '../../shared/brand/Field';
import {
  PiBriefcaseLight,
  PiCheckSquareOffsetLight,
  PiWarningLight,
  PiFileMagnifyingGlassLight,
  PiUserListLight,
  PiFileLight,
  PiCheckCircleLight,
  PiTableLight,
  PiSquaresFourLight,
  PiProjectorScreenChartLight,
  PiLinkSimpleLight,
} from 'react-icons/pi';

interface MetricsGridProps {
  overview: DashboardOverview | null;
}

interface MetricDef {
  title: string;
  value: number;
  hint: string;
  icon: React.ReactNode;
  tone?: FieldTone;
  section?: string;
  aiFeature?: string;
  route: string;
}

const MetricsGrid: React.FC<MetricsGridProps> = ({ overview }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasSectionAccess, hasAiFeatureAccess } = useLicenseGate();

  if (!overview) return null;

  const metrics: MetricDef[] = [
    {
      title: t('Active Projects'),
      value: overview.projects_active ?? 0,
      hint: t('In progress'),
      icon: <PiProjectorScreenChartLight />,
      section: 'projects',
      route: '/projects',
    },
    {
      title: t('Project Resources'),
      value: overview.project_resources_total ?? 0,
      hint: t('Linked to a project'),
      icon: <PiLinkSimpleLight />,
      section: 'projects',
      route: '/projects',
    },
    {
      title: t('Active Matters'),
      value: overview.matters,
      hint: t('Open right now'),
      icon: <PiBriefcaseLight />,
      section: 'matters',
      route: '/matters',
    },
    {
      title: t('Open Tasks'),
      value: overview.tasks_open,
      hint: t('Assigned to you'),
      icon: <PiCheckSquareOffsetLight />,
      section: 'tasks',
      route: '/tasks',
    },
    {
      title: t('Overdue Tasks'),
      value: overview.tasks_overdue,
      hint: t('Past their due date'),
      icon: <PiWarningLight />,
      tone: overview.tasks_overdue > 0 ? 'danger' : undefined,
      section: 'tasks',
      route: '/tasks',
    },
    {
      title: t('Tasks Completed'),
      value: overview.tasks_completed_30d,
      hint: t('Last 30 days'),
      icon: <PiCheckCircleLight />,
      tone: overview.tasks_completed_30d > 0 ? 'success' : undefined,
      section: 'tasks',
      route: '/tasks',
    },
    {
      title: t('Active Reviews'),
      value: overview.reviews_active,
      hint: t('Running right now'),
      icon: <PiFileMagnifyingGlassLight />,
      section: 'review',
      route: '/reviews',
    },
    {
      title: t('Clients'),
      value: overview.clients,
      hint: t('In your organisation'),
      icon: <PiUserListLight />,
      section: 'clients',
      route: '/contacts',
    },
    {
      title: t('Tables'),
      value: overview.tables,
      hint: t('Due diligence tables'),
      icon: <PiTableLight />,
      section: 'tables',
      route: '/tables',
    },
    {
      title: t('Files'),
      value: overview.files,
      hint: t('In the vault'),
      icon: <PiFileLight />,
      section: 'files',
      route: '/files',
    },
  ];

  const visibleMetrics = metrics.filter((m) => {
    if (m.section && !hasSectionAccess(m.section)) return false;
    if (m.aiFeature && !hasAiFeatureAccess(m.aiFeature)) return false;
    return true;
  });

  if (visibleMetrics.length === 0) return null;

  const gridCols = clsx(
    'widget-metrics',
    visibleMetrics.length <= 4 && 'widget-metrics-4',
    visibleMetrics.length > 4 && visibleMetrics.length <= 6 && 'widget-metrics-6',
    visibleMetrics.length > 6 && 'widget-metrics-8'
  );

  return (
    <GlassCard
      icon={<PiSquaresFourLight />}
      title={t('All Metrics')}
      padding="none"
      className="overflow-hidden"
    >
      <div className={clsx(gridCols, 'divide-x divide-y divide-hairline-soft')}>
        {visibleMetrics.map((metric) => (
          <button
            key={metric.title}
            type="button"
            onClick={() => history.push(metric.route)}
            className="text-left transition-colors duration-200 ease-slide hover:bg-surface-sunken/60 focus:bg-surface-sunken/60 focus:outline-none"
          >
            <KpiTile
              icon={metric.icon}
              value={metric.value.toLocaleString()}
              label={metric.title}
              hint={metric.hint}
              tone={metric.tone}
            />
          </button>
        ))}
      </div>
    </GlassCard>
  );
};

export default MetricsGrid;
